import { useState } from 'react';
import { cn } from '@/lib/cn';
import { Areas } from './Areas';
import { Auditoria } from './Auditoria';
import { Automatizaciones } from './Automatizaciones';
import { Calidad } from './Calidad';
import { Catalogos } from './Catalogos';
import { Copias } from './Copias';
import { Correo } from './Correo';
import { Dominio } from './Dominio';
import { Importaciones } from './Importaciones';
import { Legal } from './Legal';
import { Moderacion } from './Moderacion';
import { Panel } from './Panel';
import { Personalizacion } from './Personalizacion';
import { Plantillas } from './Plantillas';
import { Roles } from './Roles';
import { Usuarios } from './Usuarios';

/**
 * Administración — quince secciones, `#admin/<seccion>`. Módulo 17.
 *
 * Era el penúltimo archivo vanilla del portal (2.637 líneas) y el que más
 * contrato toca: 67 rutas distintas, 100 llamadas.
 *
 * ## Por qué las quince a la vez
 *
 * `admin` es **una** entrada del `PAGE_MAP` y el router solo admite `fn` o
 * `react`. Migrar media pantalla obligaría a partir la ruta, que es contrato —
 * o a montar una raíz React dentro del vanilla, que es justo lo que el proyecto
 * cerró el 2026-09-18. Es el mismo caso del módulo 15.
 *
 * ## La navegación
 *
 * Los enlaces son `<a href="#admin/...">` de verdad, como en el vanilla: cada
 * sección tiene su dirección y el Atrás del navegador funciona. Lo que cambia es
 * que la sección activa se marca con `aria-current="page"` desde el estado y no
 * tocando clases a mano en el DOM.
 *
 * `audit` es una sección REAL —`#admin/audit` funcionaba— pero nunca estuvo en
 * el menú: se llegaba solo escribiendo la dirección. Se conserva por eso, sin
 * entrada propia, porque Auditoría ya tiene la suya en el menú principal y
 * duplicar un destino es peor que dejar una dirección que alguien guardó.
 */

export interface Seccion {
  clave: string;
  icono: string;
  etiqueta: string;
}

export const GRUPOS: Array<[string | null, Seccion[]]> = [
  [null, [
    { clave: 'panel', icono: 'bi-speedometer2', etiqueta: 'Panel' },
  ]],
  ['Personas', [
    { clave: 'users', icono: 'bi-people', etiqueta: 'Usuarios' },
    { clave: 'roles', icono: 'bi-person-badge', etiqueta: 'Roles y permisos' },
    { clave: 'areas', icono: 'bi-building', etiqueta: 'Áreas' },
  ]],
  ['Contenido', [
    { clave: 'catalogs', icono: 'bi-tags', etiqueta: 'Catálogos' },
    { clave: 'templates', icono: 'bi-palette2', etiqueta: 'Plantillas' },
    { clave: 'moderation', icono: 'bi-shield-exclamation', etiqueta: 'Moderación' },
  ]],
  ['Datos', [
    { clave: 'quality', icono: 'bi-shield-check', etiqueta: 'Calidad de datos' },
    { clave: 'imports', icono: 'bi-cloud-arrow-up', etiqueta: 'Importaciones' },
    { clave: 'backups', icono: 'bi-hdd-stack', etiqueta: 'Copias y respaldo' },
  ]],
  ['Sistema', [
    { clave: 'automations', icono: 'bi-robot', etiqueta: 'Automatizaciones' },
    { clave: 'branding', icono: 'bi-brush', etiqueta: 'Personalización' },
    { clave: 'email', icono: 'bi-envelope', etiqueta: 'Correo SMTP' },
    { clave: 'domain', icono: 'bi-globe2', etiqueta: 'Dominio y SSL' },
    { clave: 'legal', icono: 'bi-shield-lock', etiqueta: 'Legal y Datos' },
  ]],
];

/** Las del menú, más la que solo se alcanza por su dirección. */
const CLAVES = [...GRUPOS.flatMap(([, s]) => s.map((x) => x.clave)), 'audit'];

/**
 * Cambiar de sección desde dentro de una sección.
 *
 * Lo usa Calidad de datos: sus hallazgos llevan a Áreas, a Catálogos o a la
 * ficha de un colaborador. El segundo argumento es esa ficha — sin él, llegar a
 * Usuarios y tener que buscar a mano a quien el informe acaba de nombrar no
 * serviría de nada.
 */
export type IrA = (seccion: string, usuario?: number) => void;

export function Administracion() {
  // Sin sub-ruta se abre el Panel, NUNCA la última sección vista: «Panel de
  // Administración» tiene que abrir siempre el Panel. Estaba escrito así en el
  // vanilla y es lo que espera quien llega desde el menú.
  const sub = String(window._routeParams?.[0] ?? '');
  const [seccion, setSeccion] = useState(() => (CLAVES.includes(sub) ? sub : 'panel'));
  const [fichaInicial, setFichaInicial] = useState<number | null>(null);

  function abrir(clave: string, usuario?: number) {
    setSeccion(clave);
    setFichaInicial(usuario ?? null);
    // Cambiar de sección no remonta la capa React: se actualiza el hash y manda
    // el estado. Sin esto, cada clic costaría volver a pedirlo todo.
    history.replaceState(null, '', `#admin/${clave}`);
  }

  return (
    <>
      <h1 className="mb-5 flex items-center gap-2 text-6 font-bold text-brand-ink">
        <i className="bi bi-gear" aria-hidden="true" /> Administración
      </h1>

      <div className="grid gap-5 lg:grid-cols-[14rem_1fr] lg:items-start">
        <nav aria-label="Secciones de administración" className="lg:sticky lg:top-4">
          {GRUPOS.map(([grupo, secciones], i) => (
            <div key={grupo ?? i} className="mb-2">
              {grupo && (
                <p className="px-3 pb-1 pt-3 text-1 font-bold uppercase tracking-wide text-muted">
                  {grupo}
                </p>
              )}
              <ul>
                {secciones.map((s) => (
                  <li key={s.clave}>
                    <a
                      href={`#admin/${s.clave}`}
                      aria-current={seccion === s.clave ? 'page' : undefined}
                      onClick={(e) => { e.preventDefault(); abrir(s.clave); }}
                      className={cn(
                        'flex items-center gap-2 rounded-sm px-3 py-2 text-3 no-underline',
                        'focus-visible:focus-anillo',
                        seccion === s.clave
                          ? 'bg-brand font-semibold text-on-brand'
                          : 'text-ink-2 hover:bg-sunken hover:text-brand-ink',
                      )}
                    >
                      <i className={`bi ${s.icono}`} aria-hidden="true" />
                      {s.etiqueta}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="min-w-0">
          <Contenido seccion={seccion} fichaInicial={fichaInicial} onIrA={abrir} />
        </div>
      </div>
    </>
  );
}

function Contenido({ seccion, fichaInicial, onIrA }: {
  seccion: string;
  fichaInicial: number | null;
  onIrA: IrA;
}) {
  switch (seccion) {
    case 'panel': return <Panel />;
    case 'users': return <Usuarios fichaInicial={fichaInicial} onIrA={onIrA} />;
    case 'roles': return <Roles />;
    case 'areas': return <Areas />;
    case 'catalogs': return <Catalogos />;
    case 'templates': return <Plantillas />;
    case 'moderation': return <Moderacion />;
    case 'quality': return <Calidad onIrA={onIrA} />;
    case 'imports': return <Importaciones />;
    case 'backups': return <Copias />;
    case 'automations': return <Automatizaciones />;
    case 'branding': return <Personalizacion />;
    case 'email': return <Correo />;
    case 'domain': return <Dominio />;
    case 'legal': return <Legal />;
    case 'audit': return <Auditoria />;
    default: return <Panel />;
  }
}
